import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Dashboard } from "@/routes/Dashboard";
import { ServiceDetail } from "@/routes/ServiceDetail";
import { EventFeed } from "@/routes/EventFeed";
import { EventDetail } from "@/routes/EventDetail";
import { AdminVenues } from "@/routes/AdminVenues";
import { DiffReview } from "@/routes/DiffReview";
import { AdminLoadIn } from "@/routes/AdminLoadIn";
import { AdminRules } from "@/routes/AdminRules";
import { PqIndex } from "@/routes/PqIndex";
import { FeedAudit } from "@/routes/FeedAudit";
import { ServiceAudit } from "@/routes/ServiceAudit";
import { useAuth } from "@/stores/auth";
import { IS_ADMIN_HOST } from "@/lib/auth";

function AdminOnly({ children }: { children: React.ReactNode }) {
  // Only the admin-dyno.* host exposes admin routes. On the public host
  // these paths redirect to the dashboard. Server-side middleware + CF Access
  // is the authoritative gate; this is UX polish.
  if (!IS_ADMIN_HOST) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const load = useAuth((s) => s.load);
  const location = useLocation();
  useEffect(() => {
    load().catch(() => {
      /* capabilities fetch handles 401 internally */
    });
  }, [load]);

  return (
    <Layout>
      {/* Keyed by pathname so navigating away from a crashed route remounts
          the boundary and clears the error. */}
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/services/:systemId" element={<ServiceDetail />} />
          <Route path="/feed" element={<EventFeed />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route path="/admin/venues" element={<AdminOnly><AdminVenues /></AdminOnly>} />
          <Route path="/admin/venues/:venue_id/diff/:document_id" element={<AdminOnly><DiffReview /></AdminOnly>} />
          <Route path="/admin/load-in" element={<AdminOnly><AdminLoadIn /></AdminOnly>} />
          <Route path="/admin/rules" element={<AdminOnly><AdminRules /></AdminOnly>} />
          <Route path="/admin/pq-index" element={<AdminOnly><PqIndex /></AdminOnly>} />
          <Route path="/admin/feeds" element={<AdminOnly><FeedAudit /></AdminOnly>} />
          <Route path="/admin/services/:systemId/audit" element={<AdminOnly><ServiceAudit /></AdminOnly>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </Layout>
  );
}
